import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import type { TopologyGraphPreset } from '@ipfabric/utils';
import { removeProp, setProp } from '@ipfabric/utils/fp/prop';
import { applyTopologySettings } from 'components/Graph/commands/applyTopologySettings';
import { clearSettings } from 'components/Graph/helpers/clearSettings';
import { isPresetValid } from 'components/Graph/helpers/isPresetValid';
import { setPresetDraftState } from 'components/Graph/helpers/setPresetDraftState';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { pipe } from 'fp-ts/lib/function';
import { type Draft, produce } from 'immer';
import { generateGuid } from 'utils/guid';

const updateGroupIds = (preset: TopologyGraphPreset): TopologyGraphPreset => {
  return produce(preset, (draft: Draft<TopologyGraphPreset>) => {
    for (const item of draft.edges) {
      if (item.type === 'group') {
        item.id = generateGuid();
      }
    }
  });
};

export interface AddTopologyPresetCommand {
  name: TopologyGraphPreset['name'];
}

export const addTopologyPreset: GraphCommandFactory<AddTopologyPresetCommand> =
  (cmdContext, dispatch) => async (command) => {
    try {
      const { presetDraft } = cmdContext.getState().layoutSettings;
      const requestPreset = pipe(presetDraft, updateGroupIds, clearSettings);

      cmdContext.updateState(setPresetDraftState('adding'));
      if (!isPresetValid(requestPreset)) {
        throw new IpfError('INVALID_ARGUMENT', 'cannot save invalid preset');
      }
      const newPreset: TopologyGraphPreset = await cmdContext.api.request({
        url: '/graphs/presets',
        method: 'POST',
        data: pipe(
          requestPreset,
          setProp('name', command.name),
          removeProp('id', 'factory'),
        ),
      });
      cmdContext.updateState((draft) => {
        draft.layoutSettings.presets.push(newPreset);
        draft.layoutSettings.presetDraft = newPreset;
      });
      cmdContext.showNotification({
        type: 'success',
        message: cmdContext.msg.canvasGraph.request.presetSaved,
      });
      dispatch(applyTopologySettings);
    } finally {
      cmdContext.updateState(setPresetDraftState('idle'));
    }
  };
